import { twJoin } from "tailwind-merge";

import { useTranslation } from "@/translations";

type OnboardingScreenProps = {
  title: string | undefined;
  subtitle: string | undefined;
  imageUrl: string;
  styling?: {
    bgColor?: string;
    textColor?: string;
  };
  withFadeOutBackground?: boolean;
  onNext?: () => void;
};

// We want to use the onboarding screens in various parts of the application. Sometimes they're smaller (in onboarding screen list), sometimes they're bigger (onboarding for residents).
// So we style this component using CQW so the relative sizes are consistent.

export function OnboardingScreen({
  imageUrl = "",
  title,
  subtitle,
  styling,
  withFadeOutBackground,
  onNext,
}: OnboardingScreenProps): React.ReactNode {
  const { t } = useTranslation();

  const ButtonComponent = onNext ? "button" : "span";

  return (
    <section className="@container h-full">
      <article
        className={twJoin(
          "group relative flex aspect-[0.5] size-full max-h-[700px] flex-col overflow-hidden rounded-lg bg-background-default-inverse bg-cover bg-center text-text-default-inverse",
        )}
        style={{
          paddingLeft: "2cqw",
          paddingRight: "2cqw",
          paddingBottom: "5cqw",
          paddingTop: "14cqw",
          backgroundImage: withFadeOutBackground
            ? `linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.35), rgba(0,0,0,1)), url(${imageUrl})`
            : `url(${imageUrl})`,
        }}
      >
        <section
          className="flex flex-1 flex-col justify-end text-center"
          style={{
            paddingLeft: "6cqw",
            paddingRight: "6cqw",
            paddingBottom: "32cqw",
            gap: "1cqw",
          }}
        >
          <h2 className="text-label-md @xs:text-heading-3">{title}</h2>
          {subtitle && <p className="text-label-xs whitespace-pre-wrap @xs:text-body-emphasis">{subtitle}</p>}
        </section>
        <ButtonComponent
          type="button"
          className="absolute inset-x-0 bg-background-brand text-center text-text-default-inverse"
          style={{
            bottom: "6cqw",
            marginLeft: "3cqw",
            marginRight: "3cqw",
            paddingTop: "3cqw",
            paddingBottom: "3cqw",
            borderRadius: "2cqw",
            fontSize: "4.5cqw",
            fontWeight: 600,
            backgroundColor: styling?.bgColor,
            color: styling?.textColor,
          }}
          data-testid="next-step-button"
          onClick={onNext}
        >
          {t("page.onboarding-screens.preview.continue")}
        </ButtonComponent>
      </article>
    </section>
  );
}
